import { useState } from "react"
import { enqueueSnackbar } from "notistack"
import { Chip, Link, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"

import { cellForWeek, CopyDishFieldKey } from "../../useWeekMenuStore"
import { useWeekMenu } from "../../WeekMenuContext"
import { cellStateFor } from "../../weekMenuLib"
import { WeekDishRow, WeekMenuCellState } from "../../weekMenuTypes"
import { WeekMenuOverwriteCheckbox } from "../dialogs/WeekMenuOverwriteCheckbox"

const ALL_FIELDS: ReadonlyArray<CopyDishFieldKey> = [
    "title",
    "description",
    "allergens",
    "price",
    "marketingTag",
    "photo",
]

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    subtitle: {
        color: theme.palette.tailwind.gray[500],
    },
    sourceCard: {
        padding: ".625rem .75rem",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".5rem",
        background: theme.palette.tailwind.gray[50],
    },
    sourceMeta: {
        color: theme.palette.tailwind.gray[500],
    },
    quickLink: {
        cursor: "pointer",
    },
    dayChip: {
        borderRadius: ".5rem",
    },
    dayChipSelected: {
        background: theme.palette.tailwind.blue[50],
        borderColor: theme.palette.tailwind.blue[600],
        color: theme.palette.tailwind.blue[800],
        fontWeight: 600,
    },
    warning: {
        padding: ".5rem .625rem",
        borderRadius: ".375rem",
        border: `1px solid ${theme.palette.tailwind.amber[200]}`,
        background: theme.palette.tailwind.amber[50],
        color: theme.palette.tailwind.amber[700],
    },
}))

type Props = Readonly<{
    open: boolean
    row: WeekDishRow
    sourceDay: number
    sourceTitle: string
    dayLabels: ReadonlyArray<string>
    onClose: () => void
}>

export function CopyDishDialog({ open, row, sourceDay, sourceTitle, dayLabels, onClose }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { actions, getWeek } = useWeekMenu()

    const [selectedDays, setSelectedDays] = useState<ReadonlyArray<number>>([])
    const [overwriteFilled, setOverwriteFilled] = useState(false)

    // A closed or theme day hides its template cells, so copying into it would write invisible dishes.
    const days = getWeek().days
    const selectableDays = dayLabels
        .map((_, dayIndex) => dayIndex)
        .filter(
            (dayIndex) =>
                dayIndex !== sourceDay &&
                row.availableDayIndexes.includes(dayIndex) &&
                !days[dayIndex].isClosed &&
                days[dayIndex].theme === null,
        )
    const selectableDaySet = new Set(selectableDays)

    const dayHasContent = (dayIndex: number) => {
        const cell = cellForWeek(getWeek(), row, dayIndex)
        const state = cellStateFor(row, cell.values, cell.isTouched)
        return state === WeekMenuCellState.FILLED || state === WeekMenuCellState.NEEDS_FILL
    }

    const willSkipSome = !overwriteFilled && selectedDays.some(dayHasContent)

    const toggleDay = (dayIndex: number) =>
        setSelectedDays((days) =>
            days.includes(dayIndex) ? days.filter((day) => day !== dayIndex) : [...days, dayIndex],
        )

    const handleCopy = () => {
        const skipped = overwriteFilled ? 0 : selectedDays.filter(dayHasContent).length
        const copied = selectedDays.length - skipped
        actions.copyDishToDays(row, sourceDay, selectedDays, ALL_FIELDS, overwriteFilled)
        enqueueSnackbar({
            variant: "success",
            message:
                skipped > 0
                    ? l10n.getString("merchant-week-menu-copy-dish-skipped-snackbar", { count: copied, skipped })
                    : l10n.getString("merchant-week-menu-copy-dish-snackbar", { count: copied }),
        })
        onClose()
    }

    return (
        <LocalizedStrict id="merchant-week-menu-copy-dish-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="Copy dish to other days" onClose={onClose}>
                <Stack spacing="1rem" className={classes.content}>
                    <LocalizedStrict id="merchant-week-menu-copy-dish-dialog-sub">
                        <Typography variant="body2" className={classes.subtitle}>
                            Copies this dish to the same row on the days you pick.
                        </Typography>
                    </LocalizedStrict>

                    <Stack spacing=".125rem" className={classes.sourceCard}>
                        <Typography variant="subtitle2">{sourceTitle || row.name}</Typography>
                        <Typography variant="caption" className={classes.sourceMeta}>
                            {row.name} · {dayLabels[sourceDay]}
                        </Typography>
                    </Stack>

                    <Stack spacing=".375rem">
                        <Stack direction="row" spacing=".75rem">
                            <LocalizedStrict id="merchant-week-menu-copy-dish-select-all">
                                <Link
                                    variant="caption"
                                    underline="hover"
                                    className={classes.quickLink}
                                    onClick={() => setSelectedDays(selectableDays)}
                                >
                                    Select all
                                </Link>
                            </LocalizedStrict>
                            <LocalizedStrict id="merchant-week-menu-copy-dish-clear">
                                <Link
                                    variant="caption"
                                    underline="hover"
                                    className={classes.quickLink}
                                    onClick={() => setSelectedDays([])}
                                >
                                    Clear
                                </Link>
                            </LocalizedStrict>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap=".375rem">
                            {dayLabels.map((label, dayIndex) => {
                                const isSource = dayIndex === sourceDay
                                const isSelected = isSource || selectedDays.includes(dayIndex)
                                const chipLabel = isSource
                                    ? `${label} ${l10n.getString("merchant-week-menu-copy-dish-source-suffix")}`
                                    : label
                                return (
                                    <Chip
                                        key={label}
                                        label={chipLabel}
                                        variant="outlined"
                                        disabled={!selectableDaySet.has(dayIndex)}
                                        className={cx(classes.dayChip, isSelected && classes.dayChipSelected)}
                                        onClick={() => toggleDay(dayIndex)}
                                    />
                                )
                            })}
                        </Stack>
                    </Stack>

                    <WeekMenuOverwriteCheckbox checked={overwriteFilled} onChange={setOverwriteFilled} />

                    {willSkipSome && (
                        <LocalizedStrict id="merchant-week-menu-copy-dish-warning">
                            <Typography variant="caption" className={classes.warning}>
                                Some selected days already have a dish. They'll be skipped unless you turn on overwrite.
                            </Typography>
                        </LocalizedStrict>
                    )}

                    <Stack direction="row" justifyContent="flex-end" spacing=".75rem">
                        <LocalizedStrict id="merchant-week-menu-copy-dish-cancel-button">
                            <StandardButton fullWidth={false} variant="outlined" color="black" onClick={onClose}>
                                Cancel
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-week-menu-copy-dish-confirm-button">
                            <StandardButton fullWidth={false} disabled={selectedDays.length === 0} onClick={handleCopy}>
                                Copy
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
